import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';

interface Props {
  modalidadeId: number;
  /** Só aparece quando o tipo de inscrição tem vagas (no total ou por grupo). */
  temVagas: boolean;
}

type OrdemFila = 'inscricao' | 'senha' | 'manual';
type SuplenteSobe = 'automatico' | 'aprovacao';

interface Config {
  aceita_suplentes: boolean;
  limite_suplentes: number | null;
  ordem_fila: OrdemFila;
  suplente_sobe: SuplenteSobe;
  prazo_pagar_suplente_horas: number | null;
  avisar_suplente: boolean;
  situacao_visivel: boolean;
}

const PADRAO: Config = {
  aceita_suplentes: true,
  limite_suplentes: null,
  ordem_fila: 'inscricao',
  suplente_sobe: 'automatico',
  prazo_pagar_suplente_horas: null,
  avisar_suplente: true,
  situacao_visivel: true,
};

const ORDENS: { valor: OrdemFila; rotulo: string }[] = [
  { valor: 'inscricao', rotulo: 'Pela hora da inscrição' },
  { valor: 'senha', rotulo: 'Pela senha da fila presencial' },
  { valor: 'manual', rotulo: 'A direção organiza' },
];

const SUBIDAS: { valor: SuplenteSobe; rotulo: string }[] = [
  { valor: 'automatico', rotulo: 'O 1º suplente entra sozinho' },
  { valor: 'aprovacao', rotulo: 'Eu aprovo antes' },
];

function Interruptor({
  ligado,
  desativado = false,
  rotulo,
  aoMudar,
}: {
  ligado: boolean;
  desativado?: boolean;
  rotulo: string;
  aoMudar: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ligado}
      aria-label={rotulo}
      onClick={aoMudar}
      disabled={desativado}
      className={`relative h-7 w-12 shrink-0 cursor-pointer rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        ligado ? 'bg-primary-500' : 'bg-secondary-300'
      }`}
    >
      <span
        className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-all ${
          ligado ? 'left-6' : 'left-1'
        }`}
      />
    </button>
  );
}

// Configuração de vagas e suplentes de um tipo de inscrição (grava direto na modalidade).
export default function BlocoVagasSuplentes({ modalidadeId, temVagas }: Props) {
  const [config, setConfig] = useState<Config>(PADRAO);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    (async () => {
      setCarregando(true);
      try {
        const { data, error } = await supabase
          .from('modalidades')
          .select(
            'aceita_suplentes, limite_suplentes, ordem_fila, suplente_sobe, prazo_pagar_suplente_horas, avisar_suplente, situacao_visivel',
          )
          .eq('id', modalidadeId)
          .maybeSingle();
        if (error) throw error;
        if (!ativo) return;
        if (data) {
          const d = data as Record<string, unknown>;
          setConfig({
            aceita_suplentes: (d.aceita_suplentes as boolean | null) ?? true,
            limite_suplentes: d.limite_suplentes === null ? null : Number(d.limite_suplentes),
            ordem_fila: ((d.ordem_fila as string | null) ?? 'inscricao') as OrdemFila,
            suplente_sobe: ((d.suplente_sobe as string | null) ?? 'automatico') as SuplenteSobe,
            prazo_pagar_suplente_horas:
              d.prazo_pagar_suplente_horas === null
                ? null
                : Number(d.prazo_pagar_suplente_horas),
            avisar_suplente: (d.avisar_suplente as boolean | null) ?? true,
            situacao_visivel: (d.situacao_visivel as boolean | null) ?? true,
          });
        }
      } catch (e) {
        if (ativo) setErro(mensagemAmigavel(e, 'Não foi possível carregar a configuração.'));
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [modalidadeId]);

  async function aplicar(patch: Partial<Config>) {
    const anterior = config;
    const novo = { ...config, ...patch };
    setConfig(novo);
    setSalvando(true);
    setErro(null);
    setSalvo(false);
    try {
      const { error } = await supabase
        .from('modalidades')
        .update(patch)
        .eq('id', modalidadeId);
      if (error) throw error;
      setSalvo(true);
      setTimeout(() => setSalvo(false), 1800);
    } catch (e) {
      setConfig(anterior);
      setErro(mensagemAmigavel(e, 'Não foi possível salvar agora.'));
    } finally {
      setSalvando(false);
    }
  }

  if (!temVagas) return null;

  if (carregando) {
    return (
      <div className="rounded-md border border-secondary-200 bg-background-50 p-4">
        <div className="h-4 w-40 animate-pulse rounded bg-secondary-200" />
        <div className="mt-3 h-3 w-full animate-pulse rounded bg-secondary-200" />
      </div>
    );
  }

  const avisoTravado = !config.situacao_visivel;

  return (
    <div className="space-y-4 rounded-md border border-secondary-200 bg-background-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="block text-base font-medium text-foreground-950">
            Vagas e suplentes
          </span>
          <p className="mt-1 text-sm text-secondary-500">
            O que acontece quando as vagas acabam e quando alguém cancela.
          </p>
        </div>
        {salvando ? (
          <span className="shrink-0 text-[12px] font-medium text-secondary-500">Salvando…</span>
        ) : salvo ? (
          <span className="shrink-0 text-[12px] font-medium text-accent-700">Salvo</span>
        ) : null}
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <span className="block text-sm font-medium text-foreground-900">
            Aceitar suplentes quando as vagas acabarem
          </span>
          <p className="mt-0.5 text-[13px] text-secondary-500">
            Quem chegar depois vai para a fila de suplentes.
          </p>
        </div>
        <Interruptor
          ligado={config.aceita_suplentes}
          rotulo="Aceitar suplentes"
          aoMudar={() => aplicar({ aceita_suplentes: !config.aceita_suplentes })}
        />
      </div>

      {config.aceita_suplentes && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="block text-sm font-medium text-foreground-900">Máximo de suplentes</span>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                value={config.limite_suplentes === null ? '' : config.limite_suplentes}
                onChange={(e) =>
                  aplicar({
                    limite_suplentes: e.target.value === '' ? null : Math.max(0, Number(e.target.value)),
                  })
                }
                placeholder="Sem limite"
                className="mt-1 w-full rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500"
              />
            </label>

            <label className="block">
              <span className="block text-sm font-medium text-foreground-900">Prazo para pagar depois de entrar</span>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                value={
                  config.prazo_pagar_suplente_horas === null
                    ? ''
                    : config.prazo_pagar_suplente_horas
                }
                onChange={(e) =>
                  aplicar({
                    prazo_pagar_suplente_horas:
                      e.target.value === '' ? null : Math.max(0, Number(e.target.value)),
                  })
                }
                placeholder="Sem prazo"
                className="mt-1 w-full rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500"
              />
              <span className="mt-1 block text-[12px] text-secondary-500">Em horas.</span>
            </label>
          </div>

          <label className="block">
            <span className="block text-sm font-medium text-foreground-900">Ordem dos suplentes</span>
            <select
              value={config.ordem_fila}
              onChange={(e) => aplicar({ ordem_fila: e.target.value as OrdemFila })}
              className="mt-1 w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500"
            >
              {ORDENS.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.rotulo}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-sm font-medium text-foreground-900">Quando alguém cancelar</span>
            <select
              value={config.suplente_sobe}
              onChange={(e) => aplicar({ suplente_sobe: e.target.value as SuplenteSobe })}
              className="mt-1 w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500"
            >
              {SUBIDAS.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.rotulo}
                </option>
              ))}
            </select>
          </label>

          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="block text-sm font-medium text-foreground-900">
                Avisar o suplente quando ele entrar
              </span>
              {avisoTravado && (
                <p className="mt-0.5 text-[12px] text-amber-700">o aviso revelaria a vaga</p>
              )}
            </div>
            <Interruptor
              ligado={config.avisar_suplente && !avisoTravado}
              desativado={avisoTravado}
              rotulo="Avisar o suplente quando ele entrar"
              aoMudar={() => aplicar({ avisar_suplente: !config.avisar_suplente })}
            />
          </div>
        </div>
      )}

      <div className="flex items-start justify-between gap-4 border-t border-secondary-200 pt-4">
        <div className="min-w-0">
          <span className="block text-sm font-medium text-foreground-900">
            O inscrito pode ver se tem vaga ou é suplente
          </span>
          <p className="mt-0.5 text-[13px] text-secondary-500">
            Desligado: o inscrito vê só &ldquo;Inscrição recebida&rdquo;. Use em acampamentos em que a
            lista é sigilosa.
          </p>
        </div>
        <Interruptor
          ligado={config.situacao_visivel}
          rotulo="O inscrito pode ver se tem vaga ou é suplente"
          aoMudar={() => aplicar({ situacao_visivel: !config.situacao_visivel })}
        />
      </div>

      {erro && (
        <p className="text-sm text-danger-600" role="alert">
          {erro}
        </p>
      )}
    </div>
  );
}